import type {
  IReservationSettings,
  IReservationSettingsConfiguration,
} from '@/interfaces/IReservationSettings';
import { maxBookableOnlineParty } from './reservation-capacity';
import { isBookableReservationTable } from './reservation-tables';
import type { IReservationSetupTable } from './reservation-tables';

export { isBookableReservationTable };
export type { IReservationSetupTable };

/** Numbers the coverage step's hint mentions. */
export type TReservationCoverageParams = { max: number; bookable: number };

export interface IReservationSetupStep {
  id: 'enabled' | 'shifts' | 'tables' | 'coverage' | 'online' | 'alerts';
  done: boolean;
  optional: boolean;
  /** Online setup cannot be acted on while reservations are off. */
  blocked: boolean;
  section: 'general' | 'shifts' | 'tables';
  /** Only the coverage step carries the sizes its hint mentions. */
  params?: TReservationCoverageParams;
}

export interface IReservationSetupState {
  steps: IReservationSetupStep[];
  complete: boolean;
  tablesWithoutSeats: number;
}

/** Bookable by mode, but no seats yet: the common state right after setup. */
export function isReservationTableWithoutSeats(
  table: IReservationSetupTable
): boolean {
  return (
    table.enabled && table.reservationsMode !== 'none' && table.capacity <= 0
  );
}

export function tablesWithoutSeatsFromStore(
  tables: readonly IReservationSetupTable[]
): number {
  return tables.filter(isReservationTableWithoutSeats).length;
}

/**
 * The Hoy notice and the automatic guide share this: no shift, or no table
 * staff can seat. Older API builds omit `shiftsCount`; the day's windows stand
 * in then. Unknown tables (store not loaded yet) never count as incomplete.
 */
export function isReservationSetupIncomplete(
  shiftsCount: number | undefined,
  dayShiftsCount: number,
  tables: readonly IReservationSetupTable[] | null
): boolean {
  const noShifts =
    shiftsCount === undefined ? dayShiftsCount === 0 : shiftsCount === 0;
  if (noShifts) return true;
  if (!tables) return false;
  return !tables.some(isBookableReservationTable);
}

/**
 * Turning reservations on suggests the signed-in email for staff alerts
 * once; an email the owner typed or cleared on purpose is never replaced.
 */
export function prefillStaffEmail(
  value: IReservationSettings,
  on: boolean,
  defaultEmail: string | null
): IReservationSettings {
  return {
    ...value,
    enabled: on,
    staffEmail: on && !value.staffEmail ? defaultEmail : value.staffEmail,
  };
}

export function getReservationSetupSteps(
  configuration: IReservationSettingsConfiguration
): IReservationSetupState {
  const hasTables = configuration.tables.some(isBookableReservationTable);
  const maxBookable = maxBookableOnlineParty(
    configuration.tables,
    configuration.tableGroups
  );
  const steps: IReservationSetupStep[] = [
    {
      id: 'shifts',
      done: configuration.shifts.length > 0,
      optional: false,
      blocked: false,
      section: 'shifts',
    },
    {
      id: 'tables',
      done: hasTables,
      optional: false,
      blocked: false,
      section: 'tables',
    },
    // Only while guests can book online and tables have seats: a group size
    // the owner accepts online but no table seats is told to call instead.
    // Optional: guests self-heal (the picker stops at what tables seat), so a
    // finished setup must not read as unfinished.
    ...(hasTables &&
    configuration.settings.enabled &&
    configuration.settings.onlineEnabled &&
    maxBookable < configuration.settings.maxPartyOnline
      ? [
          {
            id: 'coverage' as const,
            done: false,
            optional: true,
            blocked: false,
            section: 'tables' as const,
            params: {
              max: configuration.settings.maxPartyOnline,
              bookable: maxBookable,
            },
          },
        ]
      : []),
    {
      id: 'enabled',
      done: configuration.settings.enabled,
      optional: false,
      blocked: false,
      section: 'general',
    },
    {
      id: 'online',
      done:
        configuration.settings.enabled && configuration.settings.onlineEnabled,
      optional: true,
      blocked: !configuration.settings.enabled,
      section: 'general',
    },
    {
      id: 'alerts',
      done: !!configuration.settings.staffEmail,
      optional: true,
      blocked: false,
      section: 'general',
    },
  ];
  return {
    steps,
    complete: steps.every((step) => step.optional || step.done),
    tablesWithoutSeats: tablesWithoutSeatsFromStore(configuration.tables),
  };
}
